<template>
<div class="flex-body flex-container position-relative">
    <fast-table
        recordLabel="Print Groups"
        :sections="sections"
        :headers="headers"
        :data="tableData"
        :has-row-select="true"
        :activeRowId="currentGroup ? currentGroup.id : null"
        @select="onSelect"
        @click="currentGroup = $event"
    >
        <template v-if="canPrintAll" v-slot:table-actions>
            <button type="button" class="btn btn-primary btn-collapse mx-2" :disabled="!printGroups.length" @click.prevent="onPrintAll">
                <i class="far fa-fw fa-print"></i> <span class="btn-text">Print All</span>
            </button>
        </template>
    </fast-table>

    <paygroup-sidebar
        v-if="currentGroup"
        :client="sidebarClient"
        :run-id="currentGroup.paygroup_run.id"
        @close="currentGroup = null"
    />

    <div v-if="selectedGroups.length" class="print-action-bar">
        <div class="print-action-bar-label">
            {{ selectedGroups.length }} {{ selectedGroups.length == 1 ? 'row' : 'rows' }} selected:
        </div>
        <div class="print-action-bar-buttons">
            <template v-if="variant == 'held'">
                <button type="button" class="btn btn-primary" @click.prevent="onReleaseHold">
                    <i class="far fa-fw fa-arrow-left"></i> Send to Ready to Print
                </button>
            </template>
            <template v-else>
                <button type="button" class="btn btn-primary mr-1" @click.prevent="onPrintSelected">
                    <i class="far fa-fw fa-print"></i> {{ printLabel }}
                </button>
                <button v-if="variant == 'ready'" type="button" class="btn btn-outline-primary" @click.prevent="onHold">
                    <i class="far fa-fw fa-pause"></i> Hold Until Tomorrow
                </button>
                <button v-if="variant == 'printed'" type="button" class="btn btn-outline-primary" @click.prevent="onUnprint">
                    <i class="far fa-fw fa-arrow-left"></i> Un-Print
                </button>
            </template>
        </div>
    </div>

    <confirm-modal
        v-if="printModalOpen"
        :title="printLabel"
        :confirm-button-text="printLabel"
        @close="onPrintCancelled"
        @submit="onPrintConfirmed"
    >
        <p>Confirm printing of {{ printItemCount }} {{ printItemCount == 1 ? 'item' : 'items' }}?</p>
    </confirm-modal>
</div>
</template>

<script>
import FastTable from '@/components/FastTable'
import ConfirmModal from '@/components/ConfirmModal'
import PaygroupSidebar from '@/pages/client/payroll/components/PaygroupSidebar'

export default {
    props: {
        'printGroups': {type: Array, required: true},
        // 'ready': Print + Hold Until Tomorrow; 'held': Send to Ready to Print;
        // 'printed': Print (re-print) only
        'variant': {type: String, default: 'ready'},
    },
    emits: ['updated', ],
    components: {FastTable, ConfirmModal, PaygroupSidebar, },
    computed: {
        sidebarClient() {
            // PaygroupSidebar expects the payroll start screen's client object, but it
            // only uses client.id (for API calls) and client.companies (for names).
            if (!this.currentGroup) {
                return null
            }
            const run = this.currentGroup.paygroup_run
            const client = Object.assign({}, run.company.client)
            client.companies = [run.company]
            return client
        },
        printLabel() {
            // On the Printed tab the checks are already on paper, so printing again is a
            // re-print
            return this.variant == 'printed' ? 'Re-Print' : 'Print'
        },
        canPrintAll() {
            // Only the To Print tabs that offer printing; 'printed' is excluded so a
            // stray click can't re-print the whole printed history
            return this.variant == 'ready'
        },
        selectedGroups() {
            return this.printGroups.filter(group => this.selectedGroupIds.includes(group.id))
        },
        printGroupsToPrint() {
            // The Print modal covers either the current selection or, for Print All,
            // every row in the table
            return this.printAll ? this.printGroups : this.selectedGroups
        },
        printItemCount() {
            let count = 0
            this.printGroupsToPrint.forEach(group => {
                count += (group.item_count || 0)
            })
            return count
        },
        sections() {
            return [{title: 'Print Groups', defaultIsClosed: false, id: 'groups'}]
        },
        tableData() {
            return [this.printGroups.map(group => this.makeRow(group))]
        },
    },
    data() {
        return {
            selectedGroupIds: [],
            currentGroup: null,
            printModalOpen: false,
            printAll: false,
            headers: [
                {label: 'Company ID', classes: 'cw-6', isSearchable: true},
                {label: 'Company Name', classes: 'cell-company', isSearchable: true, isFilterable: true},
                {label: 'Alerts', classes: 'cw-warning', isSearchable: false},
                {label: 'Warnings', classes: 'cw-warning', type: 'warning', isSearchable: false},
                {label: 'Pay Group', classes: 'cw-8', isSearchable: true, isFilterable: true},
                {label: 'Run', classes: 'cw-3', isSearchable: false},
                {label: 'Type', classes: 'cw-4h', isSearchable: false},
                {label: 'Check Date', classes: 'cw-date', type: 'date', isSearchable: false, defaultSort: true},
                {label: 'Delivery', classes: 'cw-9', isSearchable: true, isFilterable: true},
                {label: 'Address', classes: 'cw-17', isSearchable: true},
                {label: 'Items to Print', classes: 'cw-7h cell-right', isSearchable: false},
                {label: 'Approved On', classes: 'cw-11h', type: 'datetime', isSearchable: false},
                {label: 'Client Name', classes: 'cell-company', isSearchable: true, isFilterable: true},
                {label: 'Client ID', classes: 'cw-4', isSearchable: true},
            ],
        }
    },
    watch: {
        printGroups() {
            // Drop selections for rows that are no longer in the table
            const groupIds = this.printGroups.map(group => group.id)
            this.selectedGroupIds = this.selectedGroupIds.filter(groupId => groupIds.includes(groupId))
        },
    },
    methods: {
        makeRow(group) {
            const run = group.paygroup_run
            return {
                id: group.id,
                isSelected: this.selectedGroupIds.includes(group.id),
                object: group,
                cells: [
                    run.company.company_code,
                    run.company.name,
                    '', // Alerts
                    this.checkDateWarning(group),
                    run.paygroup.name,
                    run.run_number,
                    'Regular', // Type; always Regular until off-cycle runs exist
                    run.check_date,
                    this.deliveryLabel(group),
                    this.addressLabel(group),
                    group.item_count,
                    run.processed_on,
                    run.company.client.name,
                    run.company.client.client_code,
                ],
            }
        },
        checkDateWarning(group) {
            // TODO: UI-only fudge — shows a warning count when the run's check date has
            // passed and the group still hasn't been printed. Replace with real warning
            // objects (and their click-through/sidebar plumbing) once those exist.
            if (this.variant == 'printed') {
                return ''
            }
            const now = new Date()
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
            const checkDate = group.paygroup_run.check_date
            return (checkDate && checkDate < today) ? 1 : ''
        },
        deliveryLabel(group) {
            if (!group.delivery_address) {
                return ''
            }
            return group.delivery_address.method_pretty || group.delivery_address.carrier_pretty
        },
        addressLabel(group) {
            if (!group.delivery_address) {
                return ''
            }
            const parts = [
                group.delivery_address.name,
                group.delivery_address.city,
                group.delivery_address.state,
            ]
            return parts.filter(part => part).join(', ')
        },
        onSelect(rows) {
            this.selectedGroupIds = rows.map(row => row.object.id)
        },
        onPrintSelected() {
            this.printAll = false
            this.printModalOpen = true
        },
        onPrintAll() {
            this.printAll = true
            this.printModalOpen = true
        },
        onPrintCancelled() {
            this.printModalOpen = false
            this.printAll = false
        },
        onPrintConfirmed() {
            const printGroupIds = this.printGroupsToPrint.map(group => group.id)
            this.printModalOpen = false
            this.printAll = false
            this.postAction('/admin/daily-payroll/print', printGroupIds)
        },
        onUnprint() {
            this.postAction('/admin/daily-payroll/unprint', this.selectedGroupIds)
        },
        onHold() {
            this.postAction('/admin/daily-payroll/hold', this.selectedGroupIds)
        },
        onReleaseHold() {
            this.postAction('/admin/daily-payroll/release-hold', this.selectedGroupIds)
        },
        postAction(url, printGroupIds) {
            this.$store.dispatch('START_LOADING')
            this.$api.post(url, {print_group_ids: printGroupIds}).then(() => {
                this.$store.dispatch('STOP_LOADING')
                this.selectedGroupIds = []
                this.$emit('updated')
                this.$bus.$emit('daily_payroll_updated')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
    },
}
</script>

<style scoped>
.print-action-bar {
    position: absolute;
    bottom: 20px;
    left: 1rem;
    right: 1rem;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}
</style>
